/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { useCallback, useMemo, useState, type FC, type ReactNode } from "react"
import { Calendar as CalendarIcon, X } from "lucide-react"
import { DayPicker } from "react-day-picker"
import "react-day-picker/style.css"
import { parse, format, isValid } from "date-fns"
import { viewControlsBar } from "../../styles"
import { theme, alpha } from "../../styles/theme/theme"
import { SearchableDropdown } from "./SearchableDropdown"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from "../ui/select"
import { toColumnLabel } from "../../utils/format"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import type {
  ViewControlsBarProps,
  SegmentByProps,
  AggregateByProps,
  DisplayByProps,
  FilterDropdownProps,
  DateRangeProps,
} from "./types"
import { FilterBy } from "./FilterBy"
import {
  ViewControlsContext,
  useControlDisabled,
  chipRow,
  chip,
  chipDelete,
  dropArrow,
  bareSelectTrigger,
  dropdownContentProps,
} from "./shared"

// ─── Container ────────────────────────────────────────────────────────────────

const loadingStyle = css`
  opacity: 0.5;
  pointer-events: none;
`

const Bar: FC<ViewControlsBarProps> = ({ children, disabled = false, loading = false }) => {
  const ctx = useMemo(() => ({ disabled, loading }), [disabled, loading])
  return (
    <ViewControlsContext.Provider value={ctx}>
      <div css={[viewControlsBar.container, loading && loadingStyle]}>
        {children}
      </div>
    </ViewControlsContext.Provider>
  )
}

// ─── SegmentBy ────────────────────────────────────────────────────────────────
// Atomic-webapp: Box > "Segment by" label(11px) > MultiSelect(variant=standard)
// MultiSelect renders chips with CancelIcon + ArrowDropDown, underlined.

const SegmentBy: FC<SegmentByProps> = ({
  segments,
  onSegmentsChange,
  options,
  disabled: ownDisabled = false,
  lockedValues = [],
}) => {
  const disabled = useControlDisabled(ownDisabled)
  const toggle = useCallback(
    (val: string) => {
      if (lockedValues.includes(val)) return
      if (segments.includes(val)) {
        onSegmentsChange(segments.filter((s) => s !== val))
      } else {
        onSegmentsChange([...segments, val])
      }
    },
    [segments, lockedValues, onSegmentsChange],
  )

  return (
    <div css={viewControlsBar.widget}>
      <div css={viewControlsBar.label}>Segment by</div>
      <div css={viewControlsBar.underline}>
        <div css={chipRow}>
          {segments.map((seg) => {
            const opt = options.find((o) => o.value === seg)
            const isLocked = lockedValues.includes(seg)
            return (
              <span key={seg} css={[chip, isLocked && css`opacity: 0.8;`]}>
                {opt?.label ?? toColumnLabel(seg)}
                {!isLocked && (
                  <button
                    css={chipDelete}
                    onClick={() => toggle(seg)}
                    disabled={disabled}
                    type="button"
                  >
                    <X css={css`width: 14px; height: 14px;`} />
                  </button>
                )}
              </span>
            )
          })}
        </div>
        <SearchableDropdown
          options={options}
          onSelect={toggle}
          selectedValues={segments}
          disabled={disabled}
        >
          <span css={dropArrow} />
        </SearchableDropdown>
      </div>
    </div>
  )
}

// ─── AggregateBy ──────────────────────────────────────────────────────────────
// Atomic-webapp: Box > "Aggregate by" label(11px, mb:0.1) > Select(variant=standard,
//   renderValue as Chip)

const aggItemBody = (o: { label: string; disabled?: boolean; disabledReason?: string }) =>
  o.disabled && o.disabledReason ? (
    <span
      title={o.disabledReason}
      css={css`display: flex; justify-content: space-between; gap: 0.75rem; width: 100%; align-items: center;`}
    >
      <span>{o.label}</span>
      <span css={css`font-size: 0.6875rem; color: ${theme.colors.status.error}; opacity: 0.85;`}>
        {o.disabledReason}
      </span>
    </span>
  ) : (
    o.label
  )

const AggregateBy: FC<AggregateByProps> = ({
  value,
  onChange,
  options,
  disabled: ownDisabled = false,
  label = "Aggregate by",
}) => {
  const disabled = useControlDisabled(ownDisabled)
  const selected = options.find((o) => o.value === value)
  // Grouped mode (preserves caller order) when any option declares a group;
  // otherwise the original flat, alphabetized list.
  const groups = useMemo(() => {
    if (!options.some((o) => o.group)) return null
    const order: string[] = []
    for (const o of options) {
      const g = o.group ?? ""
      if (!order.includes(g)) order.push(g)
    }
    return order.map((g) => ({
      name: g,
      caption: options.find((o) => (o.group ?? "") === g)?.groupCaption,
      items: options.filter((o) => (o.group ?? "") === g),
    }))
  }, [options])

  return (
    <div css={viewControlsBar.widget}>
      <div css={viewControlsBar.label}>{label}</div>
      <div css={viewControlsBar.underline}>
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger css={bareSelectTrigger}>
            <span css={chip}>{selected?.label ?? toColumnLabel(value)}</span>
            <span css={dropArrow} />
          </SelectTrigger>
          <SelectContent {...dropdownContentProps}>
            {groups
              ? groups.map((grp) => (
                  <SelectGroup key={grp.name}>
                    <SelectLabel>
                      {grp.name}
                      {grp.caption && (
                        <span css={css`font-weight: 400; text-transform: none; color: ${theme.colors.mutedForeground}; margin-left: 0.375rem;`}>
                          · {grp.caption}
                        </span>
                      )}
                    </SelectLabel>
                    {grp.items.map((o) => (
                      <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                        {aggItemBody(o)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))
              : [...options]
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((o) => (
                    <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                      {aggItemBody(o)}
                    </SelectItem>
                  ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

// ─── DisplayBy ────────────────────────────────────────────────────────────────
// Same pattern as AggregateBy

const DisplayBy: FC<DisplayByProps> = ({
  value,
  onChange,
  options,
  disabled: ownDisabled = false,
}) => {
  const disabled = useControlDisabled(ownDisabled)
  const selected = options.find((o) => o.value === value)
  return (
    <div css={viewControlsBar.widget}>
      <div css={viewControlsBar.label}>Display by</div>
      <div css={viewControlsBar.underline}>
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger css={bareSelectTrigger}>
            <span css={chip}>{selected?.label ?? toColumnLabel(value)}</span>
            <span css={dropArrow} />
          </SelectTrigger>
          <SelectContent {...dropdownContentProps}>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

// ─── FilterDropdown ──────────────────────────────────────────────────────────
// Labeled multi-select dropdown for a single filter dimension.
// Same visual pattern as SegmentBy but with a configurable label.
// When `floating` is true, the label acts as inline placeholder text when empty
// and appears as the small top label when values are selected. The underline
// position never shifts because the top label always reserves its height.

const floatingOuter = css`
  display: flex;
  flex-direction: column;
  margin-right: 1.5rem;
  margin-bottom: 0.5rem;
`

const floatingTopLabel = css`
  font-size: 11px;
  line-height: 1;
  min-height: 14px;
  color: ${alpha(theme.colors.foreground, 0.6)};
  margin-bottom: 2px;
  white-space: nowrap;
`

const floatingUnderline = css`
  position: relative;
  display: flex;
  align-items: center;
  height: 24px;
  border-bottom: 1px solid ${alpha(theme.colors.foreground, 0.42)};
  padding: 0 0 2px;
  &:hover {
    border-bottom: 2px solid ${alpha(theme.colors.foreground, 0.87)};
    padding-bottom: 1px;
  }
`

const floatingPlaceholder = css`
  font-size: 13px;
  line-height: 24px;
  color: ${alpha(theme.colors.foreground, 0.42)};
  white-space: nowrap;
`

const FilterDropdown: FC<FilterDropdownProps> = ({
  label,
  values,
  onChange,
  options,
  disabled: ownDisabled = false,
  clearable = true,
  multiple = true,
  floating = false,
}) => {
  const disabled = useControlDisabled(ownDisabled)
  const handleSelect = useCallback(
    (val: string) => {
      if (multiple) {
        if (values.includes(val)) {
          onChange(values.filter((v) => v !== val))
        } else {
          onChange([...values, val])
        }
      } else {
        onChange(values[0] === val ? [] : [val])
      }
    },
    [values, onChange, multiple],
  )

  if (floating) {
    const hasValues = values.length > 0
    return (
      <div css={floatingOuter}>
        <div css={[floatingTopLabel, !hasValues && css`visibility: hidden;`]}>{label}</div>
        <div css={floatingUnderline}>
          {hasValues ? (
            <div css={chipRow}>
              {values.map((val) => {
                const opt = options.find((o) => o.value === val)
                return (
                  <span key={val} css={chip}>
                    {opt?.label ?? val}
                    {clearable && (
                      <button
                        css={chipDelete}
                        onClick={() => handleSelect(val)}
                        disabled={disabled}
                        type="button"
                      >
                        <X css={css`width: 14px; height: 14px;`} />
                      </button>
                    )}
                  </span>
                )
              })}
            </div>
          ) : (
            <span css={floatingPlaceholder}>{label}</span>
          )}
          <SearchableDropdown
            options={options}
            onSelect={handleSelect}
            selectedValues={values}
            disabled={disabled}
          >
            <span css={dropArrow} />
          </SearchableDropdown>
        </div>
      </div>
    )
  }

  return (
    <div css={viewControlsBar.widget}>
      <div css={viewControlsBar.label}>{label}</div>
      <div css={viewControlsBar.underline}>
        <div css={chipRow}>
          {values.map((val) => {
            const opt = options.find((o) => o.value === val)
            return (
              <span key={val} css={chip}>
                {opt?.label ?? val}
                {clearable && (
                  <button
                    css={chipDelete}
                    onClick={() => handleSelect(val)}
                    disabled={disabled}
                    type="button"
                  >
                    <X css={css`width: 14px; height: 14px;`} />
                  </button>
                )}
              </span>
            )
          })}
        </div>
        <SearchableDropdown
          options={options}
          onSelect={handleSelect}
          selectedValues={values}
          disabled={disabled}
        >
          <span css={dropArrow} />
        </SearchableDropdown>
      </div>
    </div>
  )
}

// ─── DatePickerInput ──────────────────────────────────────────────────────────
// Mirrors atomic-webapp's DatePickerComponent: MUI DatePicker with
// variant="standard", size="small", width 160px, 20px calendar icon.

const datePickerWrapper = css`
  display: flex;
  align-items: center;
  width: 160px;
  border-bottom: 1px solid ${alpha(theme.colors.foreground, 0.42)};
  padding: 0 0 2px;
  &:hover {
    border-bottom: 2px solid ${alpha(theme.colors.foreground, 0.87)};
    padding-bottom: 1px;
  }
  &:focus-within {
    border-bottom: 2px solid ${alpha(theme.colors.foreground, 0.87)};
    padding-bottom: 1px;
  }
`

const datePickerInputStyle = css`
  flex: 1;
  font-size: ${theme.typography.fontSize.xs};
  border: none;
  background: transparent;
  color: ${alpha(theme.colors.foreground, 0.87)};
  outline: none;
  padding: 0;
  min-width: 0;
`

const calendarBtnStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  color: ${alpha(theme.colors.foreground, 0.54)};
  &:hover { color: ${alpha(theme.colors.foreground, 0.87)}; }
  &:focus { outline: none; }
`

const calendarPopoverStyle = css`
  width: auto;
  padding: 0.75rem;

  .rdp-root {
    --rdp-accent-color: ${theme.colors.blue600};
    --rdp-accent-background-color: ${alpha(theme.colors.blue600, 0.12)};
    --rdp-day-height: 32px;
    --rdp-day-width: 32px;
    --rdp-day_button-height: 32px;
    --rdp-day_button-width: 32px;
    font-size: 13px;
  }
  .rdp-month_caption {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.medium};
    padding: 0 0 ${theme.spacing.sm};
  }
  .rdp-weekday {
    font-size: ${theme.typography.fontSize.xs};
    font-weight: ${theme.typography.fontWeight.normal};
    color: ${alpha(theme.colors.foreground, 0.38)};
  }
  .rdp-day {
    font-size: 13px;
  }
  .rdp-today:not(.rdp-selected) .rdp-day_button {
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.blue600};
  }
  .rdp-selected .rdp-day_button {
    background: ${theme.colors.blue600};
    color: ${theme.colors.white};
    border-radius: 50%;
  }
`

const DATE_FMT = "MM/dd/yyyy"

/**
 * Parses a date string, trying 4-digit year first, then 2-digit.
 * Returns undefined when the value doesn't represent a valid date.
 */
function parseDateValue(value: string): Date | undefined {
  if (!value) return undefined
  let date = parse(value, "MM/dd/yyyy", new Date())
  if (isValid(date)) return date
  date = parse(value, "MM/dd/yy", new Date())
  return isValid(date) ? date : undefined
}

const DatePickerInput: FC<{
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
}> = ({ value, onChange, disabled = false, placeholder = "MM/DD/YYYY" }) => {
  const [open, setOpen] = useState(false)

  const parsedDate = useMemo(() => parseDateValue(value), [value])

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      if (date) {
        onChange(format(date, DATE_FMT))
      }
      setOpen(false)
    },
    [onChange],
  )

  return (
    <div css={datePickerWrapper}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(false)}
        placeholder={placeholder}
        disabled={disabled}
        css={datePickerInputStyle}
      />
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <button type="button" css={calendarBtnStyle} disabled={disabled}>
            <CalendarIcon css={css`width: 20px; height: 20px;`} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={4} css={calendarPopoverStyle}>
          <DayPicker
            mode="single"
            selected={parsedDate}
            onSelect={handleSelect}
            defaultMonth={parsedDate}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

// ─── DateRange ────────────────────────────────────────────────────────────────

const rangeHint = css`
  margin-left: 0.25rem;
  font-weight: 400;
  opacity: 0.75;
`

const DateRange: FC<DateRangeProps> = ({
  from,
  to,
  onFromChange,
  onToChange,
  fromOptions,
  toOptions,
  fromHint,
  toHint,
  disabled: ownDisabled = false,
}) => {
  const disabled = useControlDisabled(ownDisabled)
  if (fromOptions && toOptions) {
    return (
      <div css={css`display: flex; align-items: flex-end; gap: 1.5rem; margin-left: auto;`}>
        <div css={viewControlsBar.widget}>
          <div css={viewControlsBar.label}>
            From{fromHint ? <span css={rangeHint}>{fromHint}</span> : null}
          </div>
          <div css={[viewControlsBar.underline, css`min-width: 120px;`]}>
            <Select value={from} onValueChange={onFromChange} disabled={disabled}>
              <SelectTrigger css={bareSelectTrigger}>
                <span css={chip}>{from}</span>
                <span css={dropArrow} />
              </SelectTrigger>
              <SelectContent {...dropdownContentProps}>
                {fromOptions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div css={[viewControlsBar.widget, css`margin-right: 0;`]}>
          <div css={viewControlsBar.label}>
            To{toHint ? <span css={rangeHint}>{toHint}</span> : null}
          </div>
          <div css={[viewControlsBar.underline, css`min-width: 120px;`]}>
            <Select value={to} onValueChange={onToChange} disabled={disabled}>
              <SelectTrigger css={bareSelectTrigger}>
                <span css={chip}>{to}</span>
                <span css={dropArrow} />
              </SelectTrigger>
              <SelectContent {...dropdownContentProps}>
                {toOptions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div css={css`display: flex; align-items: flex-end; gap: 1.5rem; margin-left: auto;`}>
      <div css={viewControlsBar.widget}>
        <div css={viewControlsBar.label}>From</div>
        <DatePickerInput
          value={from}
          onChange={onFromChange}
          disabled={disabled}
        />
      </div>
      <div css={[viewControlsBar.widget, css`margin-right: 0;`]}>
        <div css={viewControlsBar.label}>To</div>
        <DatePickerInput
          value={to}
          onChange={onToChange}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

// ─── Separator ────────────────────────────────────────────────────────────────

const Separator: FC = () => (
  <div css={css`width: 1px; align-self: stretch; background: ${alpha(theme.colors.foreground, 0.12)}; margin: 0 0.25rem 0.5rem;`} />
)

// ─── Custom slot ──────────────────────────────────────────────────────────────

const Custom: FC<{ children: ReactNode }> = ({ children }) => (
  <div css={viewControlsBar.widget}>{children}</div>
)

// ─── Export as compound component ─────────────────────────────────────────────

export const ViewControls = Object.assign(Bar, {
  SegmentBy,
  AggregateBy,
  DisplayBy,
  FilterDropdown,
  FilterBy,
  DateRange,
  Separator,
  Custom,
})
